import React, { useEffect, useState } from "react"
import { ColumnDef } from "@tanstack/react-table"
import useDataTable from "hooks/useDataTable"
import { useDispatch, useSelector } from "react-redux"
import { createSelector } from "reselect"
import { FormikHelpers, useFormik } from "formik"
import DataTableTemplate from "Components/templates/data-table-template"
import Modal from "Components/molecule/modal"
import AreYouSureModal from "Components/organisms/are-you-sure-modal"
import ActionButton from "Components/atoms/buttons/action-button"
import IconButton from "Components/atoms/buttons/icon-button"
import TableRowControlsContainer from "Components/atoms/containers/table-row-controls-container"
import Chip from "Components/atoms/chip"
import FiscalYearForm from "Components/organisms/forms/fiscal-year-form"
import FiscalYearFields from "Components/organisms/forms/fiscal-year-form/types"
import { FiscalYearFormValidation } from "data/fiscal-year-form"
import FiscalYear from "models/fiscal-year"
import FiscalCalendar from "utils/fiscal-calendar"
import {
    AdminFiscalYearsSliceState,
    setFiscalYears,
    addFiscalYear,
    setFiscalYear
} from "slices/admin/fiscal-years/reducer"
import { invalidateVisibleFiscalYears } from "slices/fiscal-years/reducer"
import {
    getAllFiscalYears,
    submitCreateFiscalYearForm,
    setFiscalYearHiddenRequest
} from "slices/admin/fiscal-years/thunk"
import { PlusIcon, ViewIcon, HiddenIcon } from "Components/atoms/icons"


const selectFiscalYears = createSelector(
    (state: any) => state.AdminFiscalYears,
    ({ fiscalYears }: AdminFiscalYearsSliceState) => ({ fiscalYears })
)

const CURRENT_FISCAL_YEAR = FiscalCalendar.currentFiscalYear()

const FiscalYearsPage = () => {
    document.title = "Fiscal Years | IbexIQ";
    const dispatch: any = useDispatch()
    const { fiscalYears } = useSelector(selectFiscalYears)

    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [showFormModal, setShowFormModal] = useState<boolean>(false)
    const [focusedFiscalYear, setFocusedFiscalYear] = useState<FiscalYear | null>(null)
    const [isUpdating, setIsUpdating] = useState<boolean>(false)

    useEffect(() => {
        const init = async () => {
            const res = await getAllFiscalYears()
            if (res) dispatch(setFiscalYears(res))
            setIsLoading(false)
        }
        init()
    }, [])

    const handleCloseFormModal = () => {
        setShowFormModal(false)
        resetForm()
    }

    const onSubmit = async (vals: FiscalYearFields, helpers: FormikHelpers<FiscalYearFields>) => {
        helpers.setSubmitting(true)
        const res = await submitCreateFiscalYearForm(vals)
        if (res) {
            dispatch(addFiscalYear(res))
            dispatch(invalidateVisibleFiscalYears())
            handleCloseFormModal()
        }
        helpers.setSubmitting(false)
    }

    const {
        values,
        errors,
        touched,
        handleBlur,
        handleChange,
        handleSubmit,
        setValues,
        resetForm,
        isSubmitting,
        isValid
    } = useFormik({
        initialValues: { year: '' } as FiscalYearFields,
        onSubmit,
        validationSchema: FiscalYearFormValidation
    })

    const handleAddFiscalYear = () => {
        const next = fiscalYears.length ? Math.max(...fiscalYears.map((f: FiscalYear) => f.id)) + 1 : CURRENT_FISCAL_YEAR
        setValues({ year: next }, false)
        setShowFormModal(true)
    }

    // Hiding asks for confirmation; showing a hidden year again does not.
    const handleToggleVisibility = (fiscalYear: FiscalYear) => {
        if (fiscalYear.hide) {
            updateVisibility(fiscalYear, false)
        } else {
            setFocusedFiscalYear(fiscalYear)
        }
    }

    const handleCloseHideModal = async () => setFocusedFiscalYear(null)

    const updateVisibility = async (fiscalYear: FiscalYear, hide: boolean) => {
        setIsUpdating(true)
        const res = await setFiscalYearHiddenRequest(fiscalYear.id, hide)
        if (res) {
            dispatch(setFiscalYear(res))
            dispatch(invalidateVisibleFiscalYears())
        }
        setIsUpdating(false)
        return !!res
    }

    const confirmHideFiscalYear = async () => {
        if (!focusedFiscalYear) return
        const success = await updateVisibility(focusedFiscalYear, true)
        if (success) handleCloseHideModal()
    }

    const columns = React.useMemo<ColumnDef<FiscalYear>[]>(() => [
        {
            header: "Fiscal Year",
            accessorKey: "id",
            size: 120,
            cell: ({ row }) => <>
                FY{row.original.id}
                {row.original.id === CURRENT_FISCAL_YEAR && <span className="ms-2"><Chip variant="primary">Current</Chip></span>}
            </>
        },
        {
            header: "Range",
            id: "range",
            accessorFn: (row) => row.id,
            size: 160,
            cell: ({ row }) => `Sep ${row.original.id - 1} - Aug ${row.original.id}`
        },
        {
            header: "Periods",
            accessorKey: "periodCount",
            size: 80,
        },
        {
            header: "Visibility",
            id: "visibility",
            accessorFn: (row) => (row.hide ? "Hidden" : "Visible"),
            size: 160,
            cell: ({ row }) => <TableRowControlsContainer style={{ justifyContent: 'space-between' }}>
                {row.original.hide
                    ? <Chip variant="secondary">Hidden</Chip>
                    : <Chip variant="success">Visible</Chip>}
                <div className='d-flex flex-row justify-content-end'>
                    <IconButton
                        title={row.original.hide ? "Show in dropdowns" : "Hide from dropdowns"}
                        onClick={() => handleToggleVisibility(row.original)}
                    >
                        {row.original.hide ? <ViewIcon /> : <HiddenIcon />}
                    </IconButton>
                </div>
            </TableRowControlsContainer>
        }
    ], [])

    const table = useDataTable({
        columns,
        data: fiscalYears,
        columnResizeMode: 'onEnd',
        enableColumnResizing: true
    })

    return <DataTableTemplate
        title="Admin"
        breadcrumbItem="Fiscal Years"
        table={table}
        columns={columns}
        isLoading={isLoading}
        actions={[
            <ActionButton onClick={handleAddFiscalYear}>Add <PlusIcon /></ActionButton>
        ]}
        modal={<>
            <Modal
                isOpen={showFormModal}
                onClose={handleCloseFormModal}
                title="Create Fiscal Year"
            >
                <FiscalYearForm
                    values={values}
                    errors={errors}
                    touched={touched}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    handleSubmit={handleSubmit}
                    isValid={isValid}
                    isSubmitting={isSubmitting}
                />
            </Modal>
            <AreYouSureModal
                isOpen={!!focusedFiscalYear}
                onModalClose={handleCloseHideModal}
                modalTitle="Hide Fiscal Year"
                subTitle={`FY${focusedFiscalYear?.id ?? ''} will no longer appear in fiscal year dropdowns. Existing data is not affected. Are you sure`}
                handleAreYouSureAccept={confirmHideFiscalYear}
                handleAreYouSureDecline={handleCloseHideModal}
                areYouSureacceptText={isUpdating ? 'Hiding...' : 'Hide'}
                areYouSuredeclineText="Cancel"
            />
        </>}
    />
}

export default FiscalYearsPage
